import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  confirmPayment,
  confirmReservationPayment,
  getReservationCheckout,
  type ReservationCheckoutSession,
} from '../api/bookings';
import { HomeHeader } from '../components/home/HomeHeader';
import { ErrorIcon, SuccessIcon } from '../components/shared/Icons';
import { loadAuthSession } from '../data/authSession';
import { clearPendingCheckoutContext } from '../data/pendingCheckoutStorage';
import {
  isPaymentAlreadyConfirmed,
  normalizePaymentFailure,
  roomDetailPathFromPaymentContext,
  type PaymentFailureInfo,
} from '../utils/paymentFailure';
import '../styles/payment-result.css';

type ResultState = 'loading' | 'success' | 'error';

const CHECKOUT_CONFIRM_POLL_ATTEMPTS = 360;
const CHECKOUT_CONFIRM_POLL_INTERVAL_MS = 1000;
const CHECKOUT_FAILURE_STATES = new Set([
  'BOOKING_MODIFY_FAILED',
  'COUPON_FAILED',
  'PRE_PAYMENT_CHECK_FAILED',
  'PAYMENT_FAILED',
  'CANCELLING',
  'EXPIRED',
]);

function checkoutStepKey(checkoutId: string, step: string) {
  return `web:${checkoutId}:${step}`;
}

function checkoutFailure(session: ReservationCheckoutSession, fallback = '예약 확정에 실패했습니다.') {
  return normalizePaymentFailure({
    code: session.state,
    message:
      session.paymentFailureReason ||
      session.prePaymentFailureReason ||
      session.bookingFailureReason ||
      session.couponFailureReason ||
      fallback,
  }, fallback);
}

export function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resultState, setResultState] = useState<ResultState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const confirmKeyRef = useRef('');
  const authSession = loadAuthSession();
  const isAuthenticated = Boolean(authSession);
  const roomDetailPath = roomDetailPathFromPaymentContext(searchParams);

  const paymentKey = searchParams.get('paymentKey') ?? '';
  const orderId = searchParams.get('orderId') ?? '';
  const amount = searchParams.get('amount') ?? '';

  useEffect(() => {
    const confirmKey = `${paymentKey}:${orderId}:${amount}`;
    if (confirmKeyRef.current === confirmKey) {
      return;
    }
    confirmKeyRef.current = confirmKey;

    const showSuccess = () => {
      setResultState('success');
      sessionStorage.setItem('bander_last_payment_success_order_id', orderId);
      clearPendingCheckoutContext();
    };
    const showError = (message: string) => {
      setErrorMessage(message);
      setResultState((current) => (current === 'success' ? current : 'error'));
    };
    const delay = (ms: number) => new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
    if (sessionStorage.getItem('bander_last_payment_success_order_id') === orderId) {
      showSuccess();
      return;
    }

    const bookingId = sessionStorage.getItem('bander_pending_booking_id');
    const checkoutId = sessionStorage.getItem('bander_pending_checkout_id');
    if ((!checkoutId && !bookingId) || !paymentKey || !orderId || !amount) {
      window.setTimeout(() => showError('결제 정보가 올바르지 않습니다.'), 1000);
      return;
    }

    const waitForCheckoutCompletion = async (
      id: string,
    ): Promise<{ ok: true } | { ok: false; failure: PaymentFailureInfo }> => {
      for (let attempt = 0; attempt < CHECKOUT_CONFIRM_POLL_ATTEMPTS; attempt += 1) {
        await delay(attempt === 0 ? 500 : CHECKOUT_CONFIRM_POLL_INTERVAL_MS);
        const session = await getReservationCheckout(id);
        if (session.state === 'COMPLETED') {
          return { ok: true };
        }
        if (CHECKOUT_FAILURE_STATES.has(session.state)) {
          return { ok: false, failure: checkoutFailure(session) };
        }
      }
      return {
        ok: false,
        failure: normalizePaymentFailure(
          { code: 'PAYMENT_TIMEOUT' },
          '예약 확정이 지연되고 있습니다. 잠시 후 예약현황에서 확인해주세요.',
        ),
      };
    };

    const confirmOnce = async (): Promise<{ ok: true } | { ok: false; failure: PaymentFailureInfo }> => {
      try {
        if (checkoutId) {
          const amountWon = Number(amount);
          if (!Number.isFinite(amountWon) || amountWon <= 0) {
            return {
              ok: false,
              failure: normalizePaymentFailure({ code: 'INVALID_REQUEST' }, '결제 금액이 올바르지 않습니다.'),
            };
          }
          const current = await getReservationCheckout(checkoutId);
          if (current.state === 'COMPLETED') {
            return { ok: true };
          }
          if (['PAYMENT_CONFIRMING', 'PAYMENT_APPROVED', 'FINALIZING'].includes(current.state)) {
            return await waitForCheckoutCompletion(checkoutId);
          }
          await confirmReservationPayment(
            checkoutId,
            {
              paymentKey,
              orderId,
              amount: amountWon,
              expectedRevision: current.revision,
            },
            checkoutStepKey(checkoutId, `confirm:${orderId}`),
          );
          return await waitForCheckoutCompletion(checkoutId);
        }
        await confirmPayment(bookingId as string, { paymentKey, orderId, amount });
        return { ok: true };
      } catch (err) {
        const failure = normalizePaymentFailure(err, '결제 확인에 실패했습니다.');
        if (checkoutId) {
          try {
            const session = await getReservationCheckout(checkoutId);
            if (session.state === 'COMPLETED') {
              return { ok: true };
            }
            if (['PAYMENT_CONFIRMING', 'PAYMENT_APPROVED', 'FINALIZING'].includes(session.state)) {
              return await waitForCheckoutCompletion(checkoutId);
            }
          } catch {
            // keep the original confirm failure below
          }
        }
        return isPaymentAlreadyConfirmed(failure) ? { ok: true } : { ok: false, failure };
      }
    };

    void (async () => {
      const first = await confirmOnce();
      await delay(1000);
      const second = await confirmOnce();

      if (first.ok || second.ok) {
        showSuccess();
        return;
      }

      showError(second.failure.message || first.failure.message);
    })();
  }, [paymentKey, orderId, amount]);

  return (
    <main className="payment-result">
      <HomeHeader authenticated={isAuthenticated} onGuestCta={() => navigate('/login')} variant="icon" />
      <section className="payment-result__shell">
        {resultState === 'loading' && (
          <>
            <span aria-hidden="true" className="payment-result__loading-dot" />
            <h2 className="payment-result__title">결제 확인 중</h2>
            <p className="payment-result__description">잠시만 기다려주세요.</p>
          </>
        )}

        {resultState === 'success' && (
          <>
            <span aria-hidden="true" className="payment-result__icon payment-result__icon--success">
              <SuccessIcon />
            </span>
            <h2 className="payment-result__title">예약이 완료되었어요</h2>
            <p className="payment-result__description">업체의 승인 후 공간 사용이 가능합니다.</p>
            <div className="payment-result__actions">
              <button
                className="payment-result__button payment-result__button--secondary"
                onClick={() => navigate('/')}
                type="button"
              >
                홈으로
              </button>
              <button
                className="payment-result__button payment-result__button--primary"
                onClick={() => navigate('/my-reservations')}
                type="button"
              >
                예약현황 보기
              </button>
            </div>
          </>
        )}

        {resultState === 'error' && (
          <>
            <span aria-hidden="true" className="payment-result__icon payment-result__icon--error">
              <ErrorIcon />
            </span>
            <h2 className="payment-result__title">결제 확인에 실패했어요</h2>
            <p className="payment-result__description">{errorMessage}</p>
            <div className="payment-result__actions">
              <button
                className="payment-result__button payment-result__button--secondary"
                onClick={() => navigate('/')}
                type="button"
              >
                홈으로
              </button>
              <button
                className="payment-result__button payment-result__button--primary"
                onClick={() => navigate(roomDetailPath)}
                type="button"
              >
                확인
              </button>
            </div>
          </>
        )}
      </section>
    </main>
  );
}
